import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TID } from "@/lib/testIds";
import BrandStripe from "./BrandStripe";

const callouts = [
  { key: "recycled-lenses",     title: "Recycled Lenses",        subtitle: "Made from recycled content",  color: "var(--ge-yellow)", target: { top: "48%", left: "34%" }, label: { top: "6%",  left: "0%",   anchor: "right" } },
  { key: "recycled-copoly",     title: "Recycled Co-Polyester",  subtitle: "Bio-based frame material",    color: "var(--ge-red)",    target: { top: "42%", left: "70%" }, label: { top: "6%",  left: "100%", anchor: "left"  } },
  { key: "bpa-free",            title: "BPA Free",               subtitle: "Safe and skin-friendly",      color: "var(--ge-green)",  target: { top: "70%", left: "28%" }, label: { top: "88%", left: "0%",   anchor: "right" } },
  { key: "recycled-felt-pouch", title: "Recycled Felt Pouch",    subtitle: "Included with every pair",    color: "var(--ge-navy)",   target: { top: "58%", left: "72%" }, label: { top: "88%", left: "100%", anchor: "left"  } },
];

export const FocusPoints = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });

  const imgX = useTransform(scrollYProgress, [0, 0.4], ["-55%", "0%"]);
  const imgScale = useTransform(scrollYProgress, [0, 0.4], [1.6, 1]);
  const imgOpacity = useTransform(scrollYProgress, [0, 0.15], [0, 1]);
  const calloutOpacity = useTransform(scrollYProgress, [0.4, 0.55], [0, 1]);
  const calloutY = useTransform(scrollYProgress, [0.4, 0.6], [30, 0]);
  const hintOpacity = useTransform(scrollYProgress, [0, 0.1, 0.4, 0.5], [1, 1, 0.4, 0]);

  return (
    <section
      id="focus"
      ref={sectionRef}
      data-testid={TID.focus.root}
      className="relative bg-white"
      style={{ height: "260vh", position: "relative" }}
    >
      <div className="sticky top-0 h-screen w-full flex flex-col overflow-hidden">
        <div className="pt-24 md:pt-28">
          <div className="ge-container flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[color:var(--ge-navy)]/70 font-semibold">
            <span>Anatomy of a Good Eye</span>
            <span className="h-[1px] flex-1 bg-[color:var(--ge-navy)]/15" />
            <span className="hidden md:inline">Scroll to explore</span>
          </div>
          <div className="ge-container mt-4 md:mt-6">
            <h2 className="tight-cap font-bold text-[color:var(--ge-navy)] leading-[0.95] text-[clamp(2.2rem,5.2vw,4.4rem)]">
              Every detail,{" "}
              <span className="font-bold" style={{ color: "var(--ge-red)" }}>considered.</span>
            </h2>
            <div className="mt-4 h-[4px] w-14 rounded-full" style={{ background: "var(--ge-yellow)" }} />
          </div>
        </div>

        <div className="relative flex-1 w-full ge-container">
          <div className="absolute inset-0 flex items-center justify-center px-8 md:px-40 py-14 md:py-24">
            <div className="relative w-full max-w-[520px] aspect-[3/2] mx-auto">
              <motion.img
                src="/assets/hero/insight-evergreen-front.png"
                alt="Anatomy of Good Eyes reading frame"
                data-testid={TID.focus.frame}
                className="absolute inset-0 w-full h-full object-contain product-img z-10"
                style={{ x: imgX, scale: imgScale, opacity: imgOpacity }}
              />

              <motion.svg className="absolute inset-0 w-full h-full z-20 pointer-events-none" viewBox="0 0 100 66.6" preserveAspectRatio="none" style={{ opacity: calloutOpacity }}>
                {callouts.map((c) => {
                  const tx = parseFloat(c.target.left);
                  const ty = parseFloat(c.target.top) * 0.666;
                  const lx = parseFloat(c.label.left);
                  const ly = parseFloat(c.label.top) * 0.666;
                  const midx = (tx + lx) / 2;
                  const midy = Math.min(ty, ly) - 4;
                  return (
                    <path key={c.key} d={`M ${lx + 4} ${ly + 2} Q ${midx} ${midy} ${tx} ${ty}`} stroke="var(--ge-navy)" strokeWidth="0.2" fill="none" strokeDasharray="0.7 0.6" opacity="0.6" />
                  );
                })}
              </motion.svg>

              <motion.div className="absolute inset-0 z-20 pointer-events-none" style={{ opacity: calloutOpacity }}>
                {callouts.map((c) => (
                  <span key={c.key + "-dot"} className="absolute w-3 h-3 rounded-full" style={{ top: c.target.top, left: c.target.left, transform: "translate(-50%, -50%)", background: c.color, boxShadow: "0 0 0 4px rgba(255,255,255,0.95)" }} />
                ))}
              </motion.div>

              {callouts.map((c, idx) => (
                <motion.div
                  key={c.key}
                  data-testid={TID.focus.callout(c.key)}
                  className="absolute z-30 group"
                  style={{
                    top: c.label.top,
                    left: c.label.left,
                    opacity: calloutOpacity,
                    x: c.label.anchor === "right" ? "calc(-100% - 24px)" : "24px",
                    y: calloutY,
                  }}
                >
                  <div
                    className="flex flex-col cursor-default w-[170px] md:w-[190px]"
                    style={{
                      alignItems: c.label.anchor === "right" ? "flex-end" : "flex-start",
                      textAlign: c.label.anchor === "right" ? "right" : "left",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2 w-6 rounded-full" style={{ background: c.color }} />
                      <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/60">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="mt-1 text-sm md:text-base font-bold uppercase tracking-[0.12em] text-[color:var(--ge-navy)]">
                      {c.title}
                    </div>
                    <div className="mt-1 text-xs md:text-sm font-light text-[color:var(--ge-navy)]/75 leading-snug">
                      {c.subtitle}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div style={{ opacity: hintOpacity }} className="absolute bottom-14 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[color:var(--ge-navy)]/70 font-semibold">
            <span>Scroll to reveal</span>
            <span className="h-8 w-[1px] bg-[color:var(--ge-navy)]/40 animate-pulse" />
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <BrandStripe height={22} />
      </div>
    </section>
  );
};

export default FocusPoints;
